import { gql, useMutation } from '@apollo/client';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';

type LinkFormValues = {
  name: string;
  url: string;
};

type LinkFormModalProps = {
  title: string;
  submitLabel: string;
  initialName?: string;
  initialUrl?: string;
  isOpen: boolean;
  isSubmitting: boolean;
  onSubmit: (values: LinkFormValues) => Promise<void>;
  onClose: () => void;
};

const LinkFormModal: React.FC<LinkFormModalProps> = ({
  title,
  submitLabel,
  initialName,
  initialUrl,
  isOpen,
  isSubmitting,
  onSubmit,
  onClose,
}) => {
  const [name, setName] = React.useState(initialName ?? '');
  const [url, setUrl] = React.useState(initialUrl ?? '');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      setName(initialName ?? '');
      setUrl(initialUrl ?? '');
      setError(null);
    }
  }, [isOpen, initialName, initialUrl]);

  const isValidUrl = (value: string): boolean => {
    try {
      const u = new URL(value);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (): Promise<void> => {
    const trimmedName = name.trim();
    const trimmedUrl = url.trim();
    if (!trimmedName) {
      setError('Resource name is required.');
      return;
    }
    if (!isValidUrl(trimmedUrl)) {
      setError('Please enter a valid URL that begins with http or https.');
      return;
    }
    setError(null);
    await onSubmit({ name: trimmedName, url: trimmedUrl });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={4} isInvalid={!!error && !name.trim()}>
            <FormLabel>Resource Name</FormLabel>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Link name" />
            {!!error && !name.trim() && <FormErrorMessage>{error}</FormErrorMessage>}
          </FormControl>
          <FormControl isInvalid={!!error && !!name.trim()}>
            <FormLabel>Link URL</FormLabel>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com" />
            {!!error && !!name.trim() && <FormErrorMessage>{error}</FormErrorMessage>}
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting}>
            {submitLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ADD_LINK_AST = gql`
  mutation AddLinkModal_addLinkToGroupLibrary($input: AddLinkToGroupLibraryInput!) {
    addLinkToGroupLibrary(input: $input)
  }
`;

const UPDATE_LINK_AST = gql`
  mutation EditLinkModal_updateGroupLibraryLink($input: UpdateGroupLibraryLinkInput!) {
    updateGroupLibraryLink(input: $input)
  }
`;

type BaseLinkModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: () => void;
};

type AddLinkModalProps = BaseLinkModalProps & {
  groupId: string;
  parentLibraryFolderId: string;
};

export const AddLinkModal: React.FC<AddLinkModalProps> = ({
  groupId,
  parentLibraryFolderId,
  isOpen,
  onClose,
  onCompleted,
}) => {
  const toast = useToast();
  const [addLink, { loading }] = useMutation(ADD_LINK_AST, {
    awaitRefetchQueries: true,
    refetchQueries: ['GroupResourcesPane_LibraryFolderContents'],
  });

  const handleSubmit = async (values: LinkFormValues): Promise<void> => {
    try {
      await addLink({
        variables: {
          input: {
            groupId,
            libraryFolderId: parentLibraryFolderId,
            name: values.name,
            url: values.url,
          },
        },
      });
      toast({ status: 'success', description: 'Link added.' });
      onClose();
      onCompleted?.();
    } catch (err) {
      toast({ status: 'error', description: 'Failed to add link.' });
    }
  };

  return (
    <LinkFormModal
      title="Add Link"
      submitLabel="Add Link"
      isOpen={isOpen}
      isSubmitting={loading}
      onSubmit={handleSubmit}
      onClose={onClose}
    />
  );
};

type EditLinkModalProps = BaseLinkModalProps & {
  linkId: string;
  initialName: string;
  initialUrl: string;
};

export const EditLinkModal: React.FC<EditLinkModalProps> = ({
  linkId,
  initialName,
  initialUrl,
  isOpen,
  onClose,
  onCompleted,
}) => {
  const toast = useToast();
  const [updateLink, { loading }] = useMutation(UPDATE_LINK_AST, {
    awaitRefetchQueries: true,
    refetchQueries: ['GroupResourcesPane_LibraryFolderContents'],
  });

  const handleSubmit = async (values: LinkFormValues): Promise<void> => {
    try {
      await updateLink({
        variables: {
          input: {
            linkId,
            name: values.name,
            url: values.url,
          },
        },
      });
      toast({ status: 'success', description: 'Link updated.' });
      onClose();
      onCompleted?.();
    } catch (err) {
      toast({ status: 'error', description: 'Failed to update link.' });
    }
  };

  return (
    <LinkFormModal
      title="Rename Link"
      submitLabel="Save"
      initialName={initialName}
      initialUrl={initialUrl}
      isOpen={isOpen}
      isSubmitting={loading}
      onSubmit={handleSubmit}
      onClose={onClose}
    />
  );
};



